import React, { useEffect, useState } from 'react';
import { Box, Grid, Paper, Typography, CircularProgress, Alert, SvgIconTypeMap, useMediaQuery, useTheme } from '@mui/material';
import { OverridableComponent } from '@mui/material/OverridableComponent';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { SalesChart } from '../componets/SalesChart';
import { supabase } from '../supabaseClient';
import { LatestSalesList, Sale } from '../componets/LatestSalesList';
import { formatCompactNumber, formatCompactCurrency } from '../utils/formatters';
import { TooltipStatCard } from '../componets/TooltipStatCard';

interface DashboardStats {
    total_produtos: number;
    valor_total_estoque: number;
    produtos_estoque_baixo: number;
    produtos_sem_estoque: number;
}
interface StatCardProps {
    title: string;
    value: string | number;
    icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
    color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color }) => (
    <Paper
        elevation={3}
        sx={{
            p: { xs: 2, sm: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderRadius: 2,
            height: '100%',
            overflow: 'hidden' // Garante que nada saia do card
        }}
    >
        {/* O segredo está aqui: flex: 1 e minWidth: 0 permitem o truncamento correto */}
        <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
            <Typography
                color="text.secondary"
                variant="caption" // Fonte menor para garantir que caiba mais texto
                sx={{
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%' // Ocupa apenas o espaço disponível
                }}
                title={title}
            >
                {title}
            </Typography>
            <Typography
                component="h2"
                variant="h5"
                sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.75rem' }, // Ajuste fino no tamanho
                    whiteSpace: 'nowrap'
                }}
            >
                {value}
            </Typography>
        </Box>
        <Box sx={{
            backgroundColor: color,
            borderRadius: '50%',
            height: { xs: 42, sm: 56 },
            width: { xs: 42, sm: 56 },
            flexShrink: 0, // Impede o ícone de amassar quando o texto cresce
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: 3
        }}>
            <Icon sx={{ color: '#fff', fontSize: { xs: 22, sm: 30 } }} />
        </Box>
    </Paper>
);

export const DashboardPage: React.FC = () => {
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [latestSales, setLatestSales] = useState<Sale[]>([]);
    const [chartData, setChartData] = useState<{ name: string; Vendas: number }[]>([]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            const [statsResponse, chartResponse, salesResponse] = await Promise.all([
                supabase.rpc('get_dashboard_stats'),
                supabase.rpc('get_vendas_ultimos_7_dias'),
                supabase
                    .from('vendas')
                    .select('id, valor_liquido, data_venda, cliente:Clientes ( nome )')
                    .eq('status', 'Finalizada')
                    .order('data_venda', { ascending: false })
                    .limit(5)
            ]);

            if (statsResponse.error) {
                console.error('Erro ao buscar stats:', statsResponse.error);
                setError('Não foi possível carregar os dados do dashboard.');
            } else if (statsResponse.data) {
                setStats(statsResponse.data[0]);
            }

            if (chartResponse.error) {
                console.error("Erro no gráfico:", chartResponse.error);
            } else if (chartResponse.data) {
                const formattedChartData = chartResponse.data.map((item: any) => ({
                    name: item.dia,
                    Vendas: item.vendas
                }));
                setChartData(formattedChartData);
            }

            if (salesResponse.error) {
                console.error("Erro nas últimas vendas:", salesResponse.error);
            } else if (salesResponse.data) {
                const formattedSales = salesResponse.data.map((sale) => ({
                    id: sale.id,
                    customerName: sale.cliente?.nome ?? 'Consumidor Final',
                    totalValue: sale.valor_liquido,
                    date: new Date(sale.data_venda),
                    sellerName: 'Sistema'
                }));
                setLatestSales(formattedSales);
            }

            setLoading(false);
        };

        fetchData();
    }, []);

    const formattedStockValue = formatCompactCurrency(stats?.valor_total_estoque ?? 0);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
    }

    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold' }}>
                Dashboard
            </Typography>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Total de Produtos (Ativos)"
                        value={formatCompactNumber(stats?.total_produtos ?? 0)}
                        icon={Inventory2Icon}
                        color="#1976d2"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        title="Valor do Estoque"
                        value={formattedStockValue}
                        icon={AttachMoneyIcon}
                        color="#388e3c"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TooltipStatCard
                        fetchType="low-stock"
                        title="Estoque Baixo (<10)"
                        value={formatCompactNumber(stats?.produtos_estoque_baixo ?? 0)}
                        icon={WarningAmberIcon}
                        color="#f57c00"
                    />
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TooltipStatCard
                        fetchType="out-of-stock"
                        title="Sem Estoque"
                        value={formatCompactNumber(stats?.produtos_sem_estoque ?? 0)}
                        icon={ReportProblemIcon}
                        color="#d32f2f"
                    />
                </Grid>

                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={3} sx={{ p: 2, height: '450px', borderRadius: 2 }}>
                        <Typography variant="h6">Vendas nos últimos 7 dias</Typography>
                        <SalesChart data={chartData} />
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Paper elevation={3} sx={{ p: 2, height: isMobile ? '550px' : '450px', borderRadius: 2 }}>
                        <Typography variant="h6">Últimas vendas</Typography>
                        <LatestSalesList sales={latestSales} />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardPage;